import { useNavigate } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, CheckCircle, Users, Shuffle, Bell, Clock, XCircle, Smartphone, Calendar, MapPin } from "lucide-react";
import { PRICING_TIERS, formatPrice } from "@/lib/pricing";
import { ContentPageNav } from "@/components/ContentPageNav";
import { Footer } from "@/components/Footer";
import { ClubCarousel } from "@/components/ClubCarousel";

const TossavondOrganiseren = () => {
  const navigate = useNavigate();

  const traditionalProcess = [
    "De club organiseert een vast tossmoment (vaak wekelijks)",
    "Er worden banen vrijgehouden voor een paar uur",
    "Leden melden zich aan door hun pasje of naam in te leveren",
    "Een vrijwilliger 'tost' de spelers en maakt de indeling",
    "Er wordt gespeeld in dubbelspel, vaak op niveau gemixt",
    "Dit wisselt per 30-45 minuten",
  ];

  const problems = [
    "Clubs zijn afhankelijk van een tossleider of vrijwilliger",
    "Dit kost structureel tijd van dezelfde mensen",
    "Leden moeten teruglopen naar het clubhuis voor nieuwe indeling",
    "Dat kost speeltijd en zorgt voor onduidelijkheid",
  ];

  const appBenefits = [
    "Geen vrijwilligers meer nodig tijdens het spelen",
    "Geen tossleider of commissie nodig",
    "Leden hoeven niet terug te lopen naar het clubhuis",
    "Dat bespaart speeltijd voor iedereen",
    "Leden melden zich aan via hun mobiele telefoon",
    "Realtime de indeling zien op hun telefoon",
    "Zien wie er zijn aangemeld",
    "Meldingen bij nieuwe ronde",
    "Meldingen of de tossavond wel/niet doorgaat",
    "Volledig op afstand te gebruiken",
    "Voor zowel tennis als padel",
    "Ook geschikt voor dagtoss, seniorentoss en inloopmomenten",
  ];

  const manualTasks = [
    { num: "1", title: "Banen reserveren", description: "Iemand moet natuurlijk wel banen vrijhouden" },
    { num: "2", title: "Sessie aanmaken", description: "Een beheerder maakt het tossmoment aan" },
    { num: "3", title: "Belsignaal (optioneel)", description: "Tip: zet een alarm op je telefoon" },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <ContentPageNav showPricing />

      {/* Hero Section */}
      <header className="py-10 md:py-24 px-3">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Tossavond organiseren? Maak het eenvoudig en automatisch.
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            De tossavond is een laagdrempelige manier om leden samen te laten spelen zonder vaste afspraken. 
            Maar ook een tossavond vraagt organisatie — en die komt vaak bij dezelfde vrijwilligers terecht. 
            Met De Hussel App automatiseer je dit volledig.
          </p>
          <Button size="lg" className="gap-2 bg-accent hover:bg-accent/90 text-accent-foreground" onClick={() => navigate("/club-register")}>
            Start met automatiseren
            <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {/* Wat is een tossavond */}
      <section className="py-10 md:py-16 px-3 bg-background/90 backdrop-blur-sm">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6 md:mb-8">Wat is een tossavond?</h2>
          <div className="grid md:grid-cols-2 gap-5 md:gap-8">
            <div>
              <p className="text-muted-foreground mb-6">
                Tennis- en padelclubs organiseren vaak wekelijks tossavonden. Het principe is hetzelfde als husselen: 
                er wordt door de club een moment georganiseerd, banen worden vrijgehouden, en leden kunnen zich aanmelden.
              </p>
              <p className="text-muted-foreground mb-6">
                Tijdens een tossavond worden spelers op niveau ingedeeld. De Hussel App doet dit slim: 
                het algoritme kijkt naar speelsterkte, aantal gespeelde rondes, eerdere tegenstanders en rustperiodes 
                om elke ronde een eerlijke en gevarieerde indeling te maken.
              </p>
              <p className="text-muted-foreground">
                Het doel is ontspanning, ontmoeting en veel spelen — niet administratie.
              </p>
            </div>
            <Card className="p-4 md:p-6 bg-card border-border">
              <h3 className="font-semibold text-foreground mb-4">Bij een tossavond:</h3>
              <ul className="space-y-3">
                {traditionalProcess.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-muted-foreground">
                    <MapPin className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Het probleem */}
      <section className="py-10 md:py-16 px-3">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6 md:mb-8">De uitdaging</h2>
          <Card className="p-4 md:p-6 bg-destructive/10 border-destructive/30">
            <p className="text-muted-foreground mb-6">
              Clubs zijn vaak afhankelijk van een tossleider of vrijwilliger die de avond organiseert. 
              Dit kost structureel tijd van dezelfde mensen.
            </p>
            <ul className="space-y-3">
              {problems.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-muted-foreground">
                  <XCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </section>

      {/* De oplossing */}
      <section className="py-10 md:py-16 px-3 bg-background/90 backdrop-blur-sm">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6 md:mb-8">De Hussel App lost dit op</h2>
          <p className="text-lg text-muted-foreground mb-6 md:mb-8">
            De app ondersteunt het volledige proces:
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6 md:mb-8">
            {[
              { icon: Smartphone, title: "Online aanmelden", desc: "Leden melden zich aan via hun telefoon" },
              { icon: Shuffle, title: "Slimme automatische indeling", desc: "Het algoritme houdt rekening met speelsterkte, gespeelde rondes, eerdere tegenstanders en rustperiodes" },
              { icon: Clock, title: "Realtime overzicht", desc: "Realtime overzicht van spelers en banen" },
              { icon: Bell, title: "Push meldingen", desc: "Bij start nieuwe ronde, wijzigingen of afgelasting" },
              { icon: Users, title: "Wie is aangemeld", desc: "Iedereen ziet wie er komen spelen" },
              { icon: Calendar, title: "Volledig op afstand", desc: "Geen clubhuis of tossleider nodig die dag" },
            ].map((item, i) => (
              <Card key={i} className="p-4 md:p-6 bg-card border-border">
                <div className="bg-accent/20 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="h-6 w-6 text-accent" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </Card>
            ))}
          </div>
          
          <p className="text-center text-lg text-foreground">
            Geen fysiek bord of clubhuis nodig. Iedereen weet direct waar hij of zij moet spelen.
          </p>
        </div>
      </section>

      {/* Alle voordelen */}
      <section className="py-10 md:py-16 px-3">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6 md:mb-8">Waarom verenigingen overstappen</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {appBenefits.map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border">
                <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                <span className="text-foreground">{item}</span>
              </div>
            ))}
          </div>
        </div>
        </section>

        <ClubCarousel />

      {/* Wat moet de club doen */}
      <section className="py-10 md:py-16 px-3 bg-background/90 backdrop-blur-sm">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4 text-center">Praktisch voor bestuur en commissie</h2>
          <p className="text-muted-foreground text-center mb-6 md:mb-8">De club hoeft alleen:</p>
          <div className="grid md:grid-cols-3 gap-6">
            {manualTasks.map((item) => (
              <div key={item.num} className="text-center">
                <div className="bg-accent text-accent-foreground w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {item.num}
                </div>
                <p className="text-foreground font-medium mb-2">{item.title}</p>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-muted-foreground mt-8 italic">De rest loopt vanzelf.</p>
        </div>
      </section>

      {/* Prijzen */}
      <section className="py-10 md:py-16 px-3">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">Kosten</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {PRICING_TIERS.map((tier) => (
              <Card 
                key={tier.id}
                className={`p-6 text-center ${tier.isPopular ? 'border-2 border-accent bg-accent/5' : 'bg-card border-border'}`}
              >
                <h3 className="font-bold text-foreground mb-2">{tier.name}</h3>
                <p className="text-sm text-muted-foreground mb-4">{tier.memberRange}</p>
                <p className={`text-3xl font-bold ${tier.isPopular ? 'text-accent' : 'text-foreground'}`}>
                  €{formatPrice(tier.priceExclBTW)}
                </p>
                <p className="text-sm text-muted-foreground">per maand excl. BTW</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-16 px-3 bg-background/90 backdrop-blur-sm">
        <div className="container mx-auto max-w-4xl">
          <Card className="p-5 md:p-12 text-center bg-accent/10 border-accent/30">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Tossavond organiseren zonder vrijwilligersdruk
            </h2>
            <p className="text-lg text-muted-foreground mb-6 md:mb-8">
              Met deze oplossing blijft de tossavond laagdrempelig en gezellig, 
              zonder dat het steeds op dezelfde schouders terechtkomt.
            </p>
            <Button size="lg" className="gap-2 bg-accent hover:bg-accent/90 text-accent-foreground" onClick={() => navigate("/club-register")}>
              Registreer je Club
              <ArrowRight className="h-5 w-5" />
            </Button>
          </Card>
        </div>
      </section>


      <Footer />
    </div>
  );
};

export default TossavondOrganiseren;